import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Row,
  Col,
  Table,
  Popover,
  Modal,
  message,
  Space,
} from "antd";
import {
  SaveOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  PlusOutlined,
  CopyOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  getClientDetail,
  addOrUpdateClient,
  generateSecretKey,
  getSecretByMasterId,
  getAllApiScopes,
  getSpecifyApiScopes,
} from "../../api/idsClient";
import { getUserDropdownByUnionId } from "../../api/user";
import AvatarUpload from '../../components/Upload';
const { TextArea } = Input;
const BACK_PATH = "/admin-register";

// 授权类型（下拉数据源）
const GRANT_TYPES = [
  { value: "authorization_code", label: "授权码许可模式" },
  { value: "client_credentials", label: "客户端凭据许可模式" },
  { value: "hybrid", label: "混合许可模式" },
  { value: "implicit", label: "隐式许可模式" },
  { value: "password", label: "资源所有者许可模式" },
];
// 是否外部系统
const CommonBool = [
  { value: 0, label: "是" },
  { value: 1, label: "否" },
];

// 权限范围

// 防抖
const useDebounce = (fn: (q: string) => void, ms: number) => {
  const ref = React.useRef<ReturnType<typeof setTimeout>>();
  return useCallback(
    (q: string) => {
      if (ref.current) clearTimeout(ref.current);
      ref.current = setTimeout(() => fn(q), ms);
    },
    [fn, ms]
  );
};

const AdminRegisterDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isNew = !id || id === "new";

  const [detailLoading, setDetailLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [operationType, setOperationType] = useState<"view" | "edit">(
    isNew ? "edit" : "view"
  );
  const [baseInfo, setBaseInfo] = useState<
    Record<string, { value: number | string; name: string }[]>
  >({});
  const [scopeList, setScopeList] = useState<{ code: string; name: string }[]>(
    []
  );
  const [userList, setUserList] = useState<
    { strValue: string; strKey: string }[]
  >([]);
  const [clientSecrets, setClientSecrets] = useState<
    { content?: string; contentAfterEncrypt?: string }[]
  >([]);
  const [secretPopoverOpen, setSecretPopoverOpen] = useState(false);
  const [refreshModalOpen, setRefreshModalOpen] = useState(false);
  const [refreshSecretIndex, setRefreshSecretIndex] = useState<number | null>(
    null
  );
  const [redirectTableData, setRedirectTableData] = useState<
    { id?: string; content: string }[]
  >([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const idsRefreshTokenExpiration = [
    {
      name: "滑动过期",
      value: "0",
    },
    {
      name: "绝对过期",
      value: "1",
    },
  ];

  const loadBaseData = useCallback(async () => {
    try {
      const [dataRes, userRes] = await Promise.all([
        getAllApiScopes(),
        getUserDropdownByUnionId(),
      ]);
      // 权限范围 getAllApiScopes
      const data = dataRes as {
        res_status_code?: string;
        res_content?: { code?: string; name?: string }[];
      };
      if (data?.res_status_code === "0" && Array.isArray(data?.res_content)) {
        const list = data.res_content.map(
          (item: { code?: string; name?: string }) => ({
            code: item.code ?? item.name ?? "",
            name: item.name ?? item.code ?? "",
          })
        );
        setScopeList(list.filter((s) => s.code));
      }

      // 用户下拉（UnionId）- 系统管理员、绑定用户，返回 BaseResultKV[]，即 [{ key, value }] 或 [{ Key, Value }]
      const userData = userRes as {
        res_status_code?: string;
        res_content?: { code?: string; name?: string }[];
      };
      if (
        userData?.res_status_code === "0" &&
        Array.isArray(userData?.res_content)
      ) {
        const list = userData.res_content.map(
          (item: { code?: string; name?: string }) => ({
            code: item.code ?? "",
            name: item.name ?? "",
          })
        );
        setUserList(list.filter((s) => s.code));
      }
    } catch (e) {
      console.error("loadBaseData", e);
    }
  }, []);

  const fetchScope = useCallback(async (keyword: string) => {
    if (!keyword) return;
    try {
      const response = await getSpecifyApiScopes({ keyword });
      // Axios responses have data as the response body
      const res = response?.data ?? response;
      if (res?.res_status_code === "0" && Array.isArray(res?.res_content)) {
        const list = res.res_content.map(
          (item: { code?: string; name?: string }) => ({
            code: item.code ?? item.name ?? "",
            name: item.name ?? item.code ?? "",
          })
        );
        setScopeList(list.filter((s) => s.code));
      }
    } catch (e) {
      // 搜索失败时保留已有列表
    }
  }, []);

  const debouncedFetchScope = useDebounce(fetchScope, 400);

  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  const loadDetail = useCallback(async () => {
    if (!id || isNew) return;
    setDetailLoading(true);
    try {
      const res = await getClientDetail({ id: Number(id) });
      if (res?.res_status_code === "0" && res?.res_content) {
        const data = res.res_content;
        form.setFieldsValue({
          clientName: data.clientName,
          clientId: data.clientId,
          requireClientSecret: data.requireClientSecret,
          allowedGrantTypes: data.allowedGrantTypes,
          allowedScopes: Array.isArray(data.allowedScopes)
            ? data.allowedScopes
            : data.allowedScopes
            ? [data.allowedScopes]
            : [],
          requirePkce: data.requirePkce,
          identityTokenLifetime: data.identityTokenLifetime,
          accessTokenLifetime: data.accessTokenLifetime,
          authorizationCodeLifetime: data.authorizationCodeLifetime,
          absoluteRefreshTokenLifetime: data.absoluteRefreshTokenLifetime,
          slidingRefreshTokenLifetime: data.slidingRefreshTokenLifetime,
          refreshTokenExpiration: data.refreshTokenExpiration,
          bindUserIds: data.bindUserIds,
          systemUri: data.systemUri,
          externalSystem: data.externalSystem,
          systemAdminUser: data.systemAdminUser,
          description: data.description,
          logoUri: data.logoUri,
        });
        const secretsRes = await getSecretByMasterId({ id: Number(id) });
        const secretsList =
          secretsRes?.res_status_code === "0" && Array.isArray(secretsRes?.res_content)
            ? secretsRes.res_content.map((item) => ({
                content: item.content ?? item.value ?? item.contentAfterEncrypt,
                contentAfterEncrypt: item.contentAfterEncrypt ?? item.content ?? item.value,
              }))
            : [];
        setClientSecrets(secretsList);
        if (Array.isArray(data.redirectUris) && data.redirectUris.length > 0) {
          setRedirectTableData(
            data.redirectUris.map((c: string | { content?: string }) =>
              typeof c === "string"
                ? { content: c }
                : { content: c?.content ?? "" }
            )
          );
        } else {
          setRedirectTableData([{ content: "" }]);
        }
        if (data.allowedScopes) {
          debouncedFetchScope(
            Array.isArray(data.allowedScopes)
              ? data.allowedScopes[0]
              : data.allowedScopes
          );
        }
      } else {
        message.error(res?.res_message || "获取详情失败");
      }
    } catch (e) {
      message.error("获取详情失败");
    } finally {
      setDetailLoading(false);
    }
  }, [id, isNew, form, debouncedFetchScope]);

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({
        requirePkce: null,
        identityTokenLifetime: null,
        accessTokenLifetime: null,
        authorizationCodeLifetime: null,
        absoluteRefreshTokenLifetime: null,
        slidingRefreshTokenLifetime: null,
        refreshTokenExpiration: null,
      });
      setRedirectTableData([{ content: "" }]);
    } else {
      loadDetail();
    }
  }, [id, isNew]);

  const handleReturn = () => navigate(BACK_PATH);
  const handleEdit = () => setOperationType("edit");

  const handleAddSecret = async () => {
    try {
      const res = await generateSecretKey();
      if (res?.res_status_code === "0" && res?.res_content) {
        setClientSecrets((prev) => [...(prev || []), res.res_content]);
        message.success("生成密钥成功");
      } else {
        message.error(res?.res_message || "生成密钥失败");
      }
    } catch (e) {
      message.error("生成密钥失败");
    }
  };

  const handleRefreshSecret = async () => {
    if (refreshSecretIndex == null) return;
    try {
      const res = await generateSecretKey();
      if (res?.res_status_code === "0" && res?.res_content) {
        setClientSecrets((prev) => {
          const next = [...(prev || [])];
          next[refreshSecretIndex] = res.res_content;
          return next;
        });
        setRefreshModalOpen(false);
        setRefreshSecretIndex(null);
        message.success("刷新密钥成功");
      } else {
        message.error(res?.res_message || "刷新密钥失败");
      }
    } catch (e) {
      message.error("刷新密钥失败");
    }
  };

  const copySecret = (text: string) => {
    navigator.clipboard
      .writeText(text || "")
      .then(() => message.success("复制成功"));
  };

  const addRedirectRow = () => {
    setRedirectTableData((prev) => [...prev, { content: "" }]);
  };

  const deleteRedirectRows = () => {
    if (selectedRowKeys.length === 0) {
      message.warning("请先勾选要删除的回调地址");
      return;
    }
    setRedirectTableData((prev) =>
      prev.filter((_, idx) => !selectedRowKeys.includes(idx))
    );
    setSelectedRowKeys([]);
  };

  const updateRedirectCell = (index: number, value: string) => {
    setRedirectTableData((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], content: value };
      return next;
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const requireSecret = values.requireClientSecret;
      if (requireSecret && (!clientSecrets || clientSecrets.length === 0)) {
        message.error("请生成至少一条客户端密钥");
        return;
      }
      const list = redirectTableData.filter((r) => r.content?.trim());
      if (list.length === 0) {
        message.error("请填写至少一条系统回调地址");
        return;
      }
      const redirectUris = list.map((r) => r.content!.trim());
      const clientSecretsArr = (clientSecrets || [])
        .map((s) => s.content ?? s.contentAfterEncrypt ?? "")
        .filter(Boolean);
      const payload = {
        ...values,
        id: isNew ? 0 : Number(id),
        redirectUris,
        clientSecrets: clientSecretsArr,
      };
      setSubmitLoading(true);
      const res = await addOrUpdateClient(payload);
      if (res?.res_status_code === "0") {
        message.success(res?.res_message || "保存成功");
        navigate(BACK_PATH);
      } else {
        message.error(res?.res_message || "保存失败");
      }
    } catch (e) {
      if (e && typeof e === "object" && "errorFields" in e) return;
      message.error("保存失败");
    } finally {
      setSubmitLoading(false);
    }
  };

  const allowedGrantTypes = Form.useWatch("allowedGrantTypes", form);
  const requireClientSecret = Form.useWatch("requireClientSecret", form);
  const externalSystem = Form.useWatch("externalSystem", form);

  const isView = operationType === "view";
  const isDisabled = isView && !isNew;

  return (
    <div
      className="admin-register-detail"
      style={{ padding: 16, background: "#fff", minHeight: "100%" }}
    >
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        {isView && !isNew && (
          <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
            编辑
          </Button>
        )}
        {(!isView || isNew) && (
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={submitLoading}
            onClick={handleSave}
          >
            保存
          </Button>
        )}
        <Button icon={<ArrowLeftOutlined />} onClick={handleReturn}>
          返回
        </Button>
      </div>

      <Form
        form={form}
        layout="horizontal"
        labelAlign="right"
        colon={false}
        labelCol={{ flex: "140px" }}
        wrapperCol={{ flex: 1 }}
        disabled={isDisabled}
      >
        <Card
          loading={detailLoading}
          title="基本信息"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="clientName"
                label="客户端名称"
                rules={[{ required: true, message: "请填写客户端名称" }]}
              >
                <Input placeholder="客户端名称" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="clientId"
                label="客户端ID"
                rules={[{ required: true, message: "请填写客户端ID" }]}
              >
                <Input placeholder="客户端ID" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="systemUri"
                label="系统地址"
                rules={[{ required: true, message: "请输入系统地址" }]}
              >
                <Input placeholder="系统地址" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="externalSystem"
                label="是否外部系统"
                rules={[{ required: true, message: "请选择是否外部系统" }]}
              >
                <Select placeholder="请选择" allowClear options={CommonBool} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="systemAdminUser"
                label="系统管理员"
                rules={[{ required: true, message: "请选择系统管理员" }]}
              >
                <Select
                  placeholder="系统管理员"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={userList.map((u) => ({
                    value: u.code,
                    label: u.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={16} lg={12}>
              <Form.Item name="description" label="备注">
                <TextArea rows={2} placeholder="备注" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="logoUri"
                label="系统图标"
                rules={[{ required: true, message: "请输入系统图标地址" }]}
              >
                <AvatarUpload uploadText="上传系统图标" maxFiles={1} disabled={isDisabled} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="安全与授权" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="requireClientSecret"
                label="是否需要秘钥"
                rules={[{ required: true, message: "请选择是否需要秘钥" }]}
              >
                <Select placeholder="请选择" allowClear options={CommonBool} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="客户端密钥">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Popover
                    open={secretPopoverOpen}
                    onOpenChange={setSecretPopoverOpen}
                    content={
                      <div style={{ maxWidth: 400 }}>
                        {(clientSecrets || []).map((item, index) => (
                          <div
                            key={index}
                            style={{
                              marginBottom: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <CopyOutlined
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                copySecret(
                                  item.content ?? item.contentAfterEncrypt
                                )
                              }
                            />
                            <ReloadOutlined
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setRefreshSecretIndex(index);
                                setSecretPopoverOpen(false);
                                setRefreshModalOpen(true);
                              }}
                            />
                            <span>{item.content ?? "-"}</span>
                          </div>
                        ))}
                        {(!clientSecrets || clientSecrets.length === 0) && (
                          <span>暂无密钥</span>
                        )}
                      </div>
                    }
                    trigger="click"
                  >
                    <Button type="link" style={{ padding: 0 }}>
                      查看密钥
                    </Button>
                  </Popover>
                  {!isDisabled && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={handleAddSecret}
                    />
                  )}
                </div>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="allowedGrantTypes"
                label="授权类型"
                rules={[{ required: true, message: "请选择授权类型" }]}
              >
                <Select
                  placeholder="授权类型"
                  allowClear
                  options={GRANT_TYPES}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="requirePkce"
                label="是否使用PKCE"
                rules={[{ required: true }]}
              >
                <Select placeholder="请选择" allowClear options={CommonBool} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={16} lg={12}>
              <Form.Item
                name="allowedScopes"
                label="权限范围"
                rules={[{ required: true, message: "请选择权限范围" }]}
              >
                <Select
                  mode="multiple"
                  placeholder="权限范围"
                  allowClear
                  filterOption={false}
                  onSearch={(q) => debouncedFetchScope(q)}
                  options={scopeList.map((s) => ({
                    value: s.code,
                    label: s.name,
                  }))}
                  showSearch
                />
              </Form.Item>
            </Col>
            {allowedGrantTypes === "client_credentials" && (
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item
                  name="bindUserIds"
                  label="绑定用户"
                  rules={[{ required: true, message: "请选择绑定用户" }]}
                >
                  {/* 可以过滤绑定用户 */}
                  <Select
                    placeholder="绑定用户"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={userList.map((u) => ({
                      value: u.code,
                      label: u.name,
                    }))}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Card>

        <Card title="Token 生命周期" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="identityTokenLifetime" label="身份Token生命周期">
                <InputNumber
                  min={0}
                  controls={false}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            {/* 根据授权类型显示 */}
            {allowedGrantTypes !== "authorization_code" && (
              <>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item
                    name="accessTokenLifetime"
                    label="Access Token生命周期"
                  >
                    <InputNumber
                      min={0}
                      controls={false}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item
                    name="authorizationCodeLifetime"
                    label="授权码生命周期"
                  >
                    <InputNumber
                      min={0}
                      controls={false}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item
                    name="absoluteRefreshTokenLifetime"
                    label="刷新Token生命周期"
                  >
                    <InputNumber
                      min={0}
                      controls={false}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item
                    name="slidingRefreshTokenLifetime"
                    label="可滑动刷新Token生命周期"
                  >
                    <InputNumber
                      min={0}
                      controls={false}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="refreshTokenExpiration"
                label="刷新时间类型"
                rules={[{ required: true }]}
              >
                <Select
                  placeholder="请选择"
                  allowClear
                  options={idsRefreshTokenExpiration.map((r) => ({
                    value: Number(r.value),
                    label: r.name,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title="回调地址"
          extra={
            <Space>
              <Button disabled={isDisabled} onClick={deleteRedirectRows}>
                删除
                {selectedRowKeys.length ? ` (${selectedRowKeys.length})` : ""}
              </Button>
              <Button
                type="primary"
                disabled={isDisabled}
                icon={<PlusOutlined />}
                onClick={addRedirectRow}
              >
                新建
              </Button>
            </Space>
          }
        >
          <Table
            rowKey={(_, index) => index}
            size="small"
            pagination={false}
            dataSource={redirectTableData}
            rowSelection={
              isDisabled
                ? undefined
                : { selectedRowKeys, onChange: setSelectedRowKeys }
            }
            columns={[
              { title: "序号", width: 60, render: (_, __, index) => index + 1 },
              {
                title: "回调地址",
                dataIndex: "content",
                render: (val, record, index) => (
                  <Input
                    value={redirectTableData[index]?.content}
                    disabled={isDisabled}
                    onChange={(e) => updateRedirectCell(index, e.target.value)}
                    placeholder="回调地址"
                  />
                ),
              },
            ]}
          />
        </Card>
      </Form>

      <Modal
        open={refreshModalOpen}
        title="确认是否刷新密钥？"
        onCancel={() => {
          setRefreshModalOpen(false);
          setRefreshSecretIndex(null);
        }}
        onOk={handleRefreshSecret}
        okText="确定"
        cancelText="取消"
      >
        <span>刷新后原始密钥会失效，请谨慎操作</span>
      </Modal>
    </div>
  );
};

export default AdminRegisterDetail;
